import {
    confirmLibraryUpload,
    getApiErrorMessage,
    getPresignedUploadUrl,
    IApiAxiosError,
    IClientMinimal,
    IIndustry,
    ILibraryDocument,
    ILibraryFolder,
    uploadToS3,
} from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    MAX_FILE_SIZE_BYTES,
    MAX_FILE_SIZE_MB,
    PDF_ACCEPT,
    PDF_SPREADSHEET_ACCEPT,
} from "@/config/constants";
import { trackDocumentUploaded } from "@/lib/analytics";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { buildFolderOptions } from "../utils/folderOptions";
import { UploadDropzone } from "./upload/UploadDropzone";
import { UploadFileList } from "./upload/UploadFileList";
import type { FileUploadItem } from "./upload/types";

interface UploadDialogProps {
    open: boolean;
    onClose: () => void;
    folderId: number | null;
    isKnowledgeBase: boolean;
    folders: ILibraryFolder[];
    onUploadComplete: () => void;
    /** Pre-tag confirmed uploads with this client. Client-scoped surfaces
     *  (ClientLibraryTab's New menu) pass it so content created there is
     *  visible there; explicit client_id supersedes folder inheritance
     *  server-side. */
    defaultClient?: IClientMinimal | null;
    /** The client root folder id for client-scoped uploads. When set, the
     *  folder select lists only the client subtree (the caller pre-filters
     *  `folders`); the root itself is omitted from the options because an
     *  EMPTY selection IS the root. So when the current `folderId` is the
     *  client root (or null), the select shows empty, and an empty submit
     *  uploads to the client root, never the org library root. Omit in the
     *  main library (empty stays empty -> org root, unchanged). */
    rootFolderId?: number | null;
    /** Files dropped on a page-level drop zone before the dialog opened.
     *  Consumed once per open through the same onDrop path as the inner
     *  dropzone, so S3 uploads start immediately. Callers pre-validate
     *  (type/size); the prop does not re-filter. */
    initialFiles?: File[];
    /** Accept `.xlsx`/`.csv` drops in addition to PDF. Defaults to `true`:
     *  UploadDialog is used ONLY by library-upload surfaces, and every library
     *  surface accepts spreadsheets, so a safe default keeps a caller from
     *  silently regressing to PDF-only by forgetting the prop (which is exactly
     *  how CreateMenu/ClientDetail shipped broken). Independent of
     *  `isKnowledgeBase`/`uploadingToKb` -- the "Knowledge Base" banner stays
     *  keyed on KB-ness. Pass `false` only for a hypothetical PDF-only surface;
     *  the scope/comparison pickers use ModalFileDropzone, not this dialog. */
    allowSpreadsheets?: boolean;
}

export const UploadDialog = ({
    open,
    onClose,
    folderId,
    isKnowledgeBase,
    folders,
    onUploadComplete,
    defaultClient,
    rootFolderId = null,
    initialFiles,
    allowSpreadsheets = true,
}: UploadDialogProps) => {
    const queryClient = useQueryClient();
    const [files, setFiles] = useState<FileUploadItem[]>([]);
    // At the client root, show the select empty (empty == root); elsewhere
    // preselect the current folder. In the main library `rootFolderId` is
    // null, so this reduces to `folderId ?? ""`. Reused by the open effect and
    // the reset-on-close so the initial, re-open, and reset values agree.
    const initialFolderSelection: number | "" =
        folderId == null || folderId === rootFolderId ? "" : folderId;
    const [selectedFolderId, setSelectedFolderId] = useState<number | "">(
        initialFolderSelection
    );
    const [selectedIndustry] = useState<IIndustry | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const nextIdRef = useRef(0);
    const initialFilesConsumedRef = useRef(false);

    useEffect(() => {
        if (open) setSelectedFolderId(initialFolderSelection);
    }, [open, initialFolderSelection]);

    const selectedFolder = folders.find((f) => f.id === selectedFolderId);
    const uploadingToKb = selectedFolder?.is_knowledge_base ?? isKnowledgeBase;

    const isAnyUploading = files.some((f) => f.status === "uploading");
    const readyCount = files.filter((f) => f.status === "ready").length;

    // base-ui's `<SelectValue>` renders the raw folder-id string by default;
    // feed the Root an `items` map so the trigger shows the folder name.
    // Exclude the client root from the options: an empty selection already
    // means the root, so listing it would be a redundant second path.
    const folderItems = buildFolderOptions(
        folders,
        rootFolderId != null ? [rootFolderId] : undefined
    )
        .filter((opt) => opt.type !== "header")
        .map(({ value, label, isShared }) => ({ value, label, isShared }));

    const uploadFileToS3 = useCallback(async (item: FileUploadItem) => {
        // Which step failed drives both the Sentry tag and the per-file
        // reason; "upload keeps failing" reports are unactionable when
        // presign vs S3 PUT vs network are indistinguishable.
        let phase: "presign" | "transfer" = "presign";
        try {
            const presigned = await getPresignedUploadUrl(item.file.name);
            phase = "transfer";
            await uploadToS3(presigned.upload_url, item.file, (progress) => {
                setFiles((prev) =>
                    prev.map((f) => (f.id === item.id ? { ...f, progress } : f))
                );
            });
            setFiles((prev) =>
                prev.map((f) =>
                    f.id === item.id
                        ? {
                              ...f,
                              objectKey: presigned.object_key,
                              status: "ready" as const,
                          }
                        : f
                )
            );
        } catch (err) {
            Sentry.captureException(err, {
                tags: { feature: "library-upload", phase },
                extra: { fileName: item.file.name, byteSize: item.file.size },
            });
            const errorReason =
                phase === "presign"
                    ? "Couldn't start the upload: failed to get an upload URL."
                    : "Couldn't upload to storage: network or storage error.";
            setFiles((prev) =>
                prev.map((f) =>
                    f.id === item.id
                        ? { ...f, status: "failed" as const, errorReason }
                        : f
                )
            );
        }
    }, []);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const newItems: FileUploadItem[] = acceptedFiles.map((file) => ({
                id: String(nextIdRef.current++),
                file,
                objectKey: null,
                progress: 0,
                status: "uploading" as const,
            }));
            setFiles((prev) => [...prev, ...newItems]);
            newItems.forEach((item) => uploadFileToS3(item));
        },
        [uploadFileToS3]
    );

    // Seed externally dropped files once per open; the ref (not state)
    // survives StrictMode's double effect.
    useEffect(() => {
        if (!open) {
            initialFilesConsumedRef.current = false;
            return;
        }
        if (initialFiles?.length && !initialFilesConsumedRef.current) {
            initialFilesConsumedRef.current = true;
            onDrop(initialFiles);
        }
    }, [open, initialFiles, onDrop]);

    const handleRemoveFile = useCallback((id: string) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const handleClose = useCallback(() => {
        if (isConfirming) return;
        setFiles([]);
        setSelectedFolderId(initialFolderSelection);
        setError(null);
        setIsConfirming(false);
        onClose();
    }, [isConfirming, initialFolderSelection, onClose]);

    const handleConfirm = async () => {
        const readyFiles = files.filter((f) => f.status === "ready");
        if (readyFiles.length === 0) {
            setError("Please add files");
            return;
        }

        setIsConfirming(true);
        setError(null);

        // Empty selection resolves to the client root when one is in play
        // (keeps client uploads inside the workspace), else org-level null.
        const resolvedFolderId =
            selectedFolderId !== "" ? selectedFolderId : (rootFolderId ?? null);

        try {
            const confirmedDocs: ILibraryDocument[] = [];
            for (const item of readyFiles) {
                const doc = await confirmLibraryUpload({
                    object_key: item.objectKey!,
                    document_name: item.file.name,
                    folder_id: resolvedFolderId,
                    industry_id: selectedIndustry?.id ?? null,
                    client_id: defaultClient?.id ?? null,
                });
                confirmedDocs.push(doc);
                trackDocumentUploaded({
                    library_document_id: doc.id,
                    folder_id: resolvedFolderId,
                    is_knowledge_base: uploadingToKb,
                    source: "library_upload",
                });
            }

            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });
            queryClient.invalidateQueries({ queryKey: ["library-documents"] });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-folders"],
            });

            const isProcessing = confirmedDocs.some(
                (d) =>
                    d.textract_status !== "processed" &&
                    d.textract_status !== "failed"
            );
            toast.success(
                readyFiles.length === 1
                    ? "Document uploaded successfully"
                    : `${readyFiles.length} documents uploaded successfully`,
                {
                    description: isProcessing
                        ? "Processing started - this will take a few minutes."
                        : undefined,
                }
            );
            handleClose();
            onUploadComplete();
        } catch (err) {
            setError(
                getApiErrorMessage(
                    err as IApiAxiosError,
                    "Failed to save documents"
                )
            );
            setIsConfirming(false);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        // Spreadsheet acceptance is an explicit capability, independent of
        // whether the destination is a Knowledge Base folder.
        accept: allowSpreadsheets ? PDF_SPREADSHEET_ACCEPT : PDF_ACCEPT,
        maxSize: MAX_FILE_SIZE_BYTES,
        disabled: isConfirming,
    });

    const confirmLabel = isConfirming
        ? "Saving..."
        : isAnyUploading
          ? "Uploading..."
          : `Upload${readyCount > 1 ? ` (${readyCount})` : ""}`;

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !isConfirming) handleClose();
            }}
        >
            <DialogContent className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
                <DialogHeader className="border-b p-4 pr-8">
                    <DialogTitle>Upload Documents</DialogTitle>
                    <DialogDescription>
                        Documents will be immediately processed
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleConfirm();
                    }}
                    className="flex min-h-0 flex-1 flex-col"
                >
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
                        {uploadingToKb && (
                            <div className="border-primary/15 bg-primary/5 rounded-md border p-3 text-sm">
                                Uploading to Knowledge Base will automatically
                                process documents for AI-powered search.
                            </div>
                        )}

                        <UploadDropzone
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isDragActive={isDragActive}
                            idleLabel={
                                allowSpreadsheets
                                    ? "Drop PDFs, Excel or CSV here or click to browse"
                                    : "Drop PDFs here or click to browse"
                            }
                            hint={
                                allowSpreadsheets
                                    ? `PDF, XLSX or CSV up to ${MAX_FILE_SIZE_MB}MB each`
                                    : `PDF files up to ${MAX_FILE_SIZE_MB}MB each`
                            }
                        />

                        <UploadFileList
                            items={files}
                            onRemove={handleRemoveFile}
                            removeDisabled={isConfirming}
                        />

                        {folderItems.length > 0 && (
                            <Select
                                items={folderItems}
                                value={
                                    selectedFolderId !== ""
                                        ? String(selectedFolderId)
                                        : ""
                                }
                                onValueChange={(value) =>
                                    setSelectedFolderId(
                                        value ? parseInt(value) : ""
                                    )
                                }
                            >
                                <SelectTrigger
                                    aria-label="Destination folder"
                                    className="w-full"
                                >
                                    <SelectValue placeholder="Select folder..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {folderItems.map(
                                        ({ value, label, isShared }) => (
                                            <SelectItem
                                                key={value}
                                                value={value}
                                            >
                                                {label}
                                                {isShared && (
                                                    <Badge
                                                        variant="secondary"
                                                        className="ml-2"
                                                    >
                                                        Shared with group
                                                    </Badge>
                                                )}
                                            </SelectItem>
                                        )
                                    )}
                                </SelectContent>
                            </Select>
                        )}

                        {error && (
                            <p className="text-destructive text-sm">{error}</p>
                        )}
                    </div>

                    <DialogFooter className="mx-0 mb-0">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isConfirming}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                isConfirming ||
                                isAnyUploading ||
                                readyCount === 0
                            }
                        >
                            {isConfirming || isAnyUploading ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    {confirmLabel}
                                </>
                            ) : (
                                confirmLabel
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
